import { createSlice, SerializedError } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import type { PostgrestError } from '@supabase/supabase-js'
import pick from 'lodash/pick'
import { PURGE } from 'redux-persist';
import omit from 'lodash/omit'
import { GeneratedColumns, isTableWithGeneratedColumns, SyncableTable, SyncableTables, SyncAction } from '../lib/sync/action'
import { findPendingAction, findPendingActionIndex, isServerRejection, isUnrecoverable, UnsyncableRecord } from '../lib/sync/unrecoverable'
import { SyncResult } from '../lib/sync/action'
import { addExpense, deleteExpense, updateExpense, redactExpense, CompleteExpensePaymentPayload, completeExpensePayment } from './expensesSlice'
import { createAttachment, deleteAttachment, redactAttachment, updateAttachment } from './attachmentsSlice'
import { addIncident, deleteIncident, updateIncident, redactIncident, markHasCalledCHMForPregnancy } from './incidentsSlice'
import { addSubmission, deleteSubmission, markSubmissionAsRepaid, redactSubmission, updateSubmission } from './submissionsSlice'
import { isDeleted } from '../types/supabase'
import { DeepNullable } from '../types/util'
import { onSyncDownComplete, OnSyncDownCompletePayload } from './actions/onSyncDownComplete';
import { createAdvanceRequest, deleteAdvanceRequest, indicateAdvanceRepaid, redactAdvance, redactAdvanceRequest } from './advancesSlice';
import { markNotificationAsRead, markNotificationAsUnread, redactNotification } from './notificationsSlice';
import merge from 'lodash/merge';
import { addProvider, updateProvider, deleteProvider, redactProvider } from './providersSlice';
import { completeTodo, redactTodo } from './todosSlice';
import { onBlobCreated, updateBlob, deleteBlob, redactBlob } from './blobsSlice';
import { addConversation, updateConversation, addMessage, updateMessage, addPhoneCall } from './conversationsSlice';
import { cloneDeep } from 'lodash';

export type SyncSliceState = {
  pending: SyncAction<SyncableTable>[]
  errored: SyncResult['error']
  lastSync?: SyncResult | null
  lastSyncedAt?: number | null

  error?: SerializedError | null

  deletedLocally?: {
    [table in SyncableTable]?: Array<{ id: string, updated_at: string, deleted_at: string }>
  }

  /**
   * Records the server refused often enough that we stopped trying (see
   * lib/sync/unrecoverable.ts). Sync-down consults this so it doesn't re-queue a
   * record we just discarded; the entry is dropped as soon as the record changes
   * again locally, because a new version deserves a fresh set of attempts.
   */
  unsyncable?: {
    [table in SyncableTable]?: Array<UnsyncableRecord>
  }

  /**
   * The sync-down watermark, as issued by the server. Selects which sync-down mode runs:
   * present means the cheap `since` fast path, absent means a full `have` reconcile.
   *
   * Stored exactly as received and never computed locally. `updated_at` on our own writes
   * comes from this browser's clock, so a machine running fast would otherwise push the
   * cursor into the future and hide every server write until real time caught up.
   *
   * Cleared when the membership changes — a watermark from one membership says nothing
   * about another's history, and carrying it over would silently skip everything older.
   */
  cursor?: string | null
}

const initialState: SyncSliceState = {
  pending: [],
  errored: []
}

const SyncActions = [
  ['add', 'expenses', addExpense],
  ['update', 'expenses', updateExpense],
  ['delete', 'expenses', deleteExpense],
  ['add', 'incidents', addIncident],
  ['update', 'incidents', updateIncident],
  ['delete', 'incidents', deleteIncident],
  ['add', 'submissions', addSubmission],
  ['update', 'submissions', updateSubmission],
  ['delete', 'submissions', deleteSubmission],
  ['add', 'attachments', createAttachment],
  ['update', 'attachments', updateAttachment],
  ['delete', 'attachments', deleteAttachment],
  ['add', 'advance_requests', createAdvanceRequest],
  ['delete', 'advance_requests', deleteAdvanceRequest],
  ['update', 'advances', indicateAdvanceRepaid],
  ['update', 'notifications', markNotificationAsRead],
  ['update', 'notifications', markNotificationAsUnread],
  ['add', 'providers', addProvider],
  ['update', 'providers', updateProvider],
  ['delete', 'providers', deleteProvider],
  // Todos are created and deleted only by the backend reconciliation process --
  // there is deliberately no ['add', 'todos', ...] or ['delete', 'todos', ...]
  // mapping. The one todo write the client owns is the member marking one done,
  // so that update is pushed explicitly rather than relying on syncDown's
  // divergence-repair pass to notice it.
  ['update', 'todos', completeTodo],
  // Capture is local-first (Cleanup C, docs/plans/native-app-sdk-seams.md): onBlobCreated's
  // payload is already a full row with id/updated_at, so it doubles as both the local
  // optimistic insert and the sync queue entry -- no separate `addBlob` action needed.
  ['add', 'blobs', onBlobCreated],
  ['update', 'blobs', updateBlob],
  ['delete', 'blobs', deleteBlob],
  // Conversations and messages must sync up as soon as they're created, not
  // just when the next syncDown reconciliation notices they're missing on the
  // server. SyncController::askAliceToReply then dispatches
  // ProcessAliceConversationJob from applied inbound message adds.
  ['add', 'conversations', addConversation],
  ['update', 'conversations', updateConversation],
  ['add', 'messages', addMessage],
  ['update', 'messages', updateMessage],
  // Requesting an AI call is the one phone_calls write a member owns -- there is
  // deliberately no ['update', ...] or ['delete', ...] mapping, and the sync endpoint
  // refuses both. Everything about how the call went is written by the Retell webhooks.
  // The AI-call screen used to insert this row straight through PostgREST, which meant
  // no transaction, a client-supplied initiated_by, and an RLS policy granting `for all`.
  ['add', 'phone_calls', addPhoneCall],
] as const

export const syncSlice = createSlice({
  name: 'supabase',
  initialState,
  reducers: {
    onSyncUpComplete: (state, action: PayloadAction<SyncResult>) => {
      const result = action.payload;
      const {pending, errored} = state;

      // Remove all the pending actions where we successfully completed the query to the server.
      // Match on fields, never by reference: `pending` here is an Immer draft, which hands
      // back a fresh proxy on every index read, so `indexOf` of the plain action the sync
      // actor read out of `getState()` is always -1 -- and `splice(-1, 1)` drops whatever
      // happens to be at the END of the queue. That silently deleted actions the user
      // queued while the sync pass was in flight (a conversation and its first message,
      // in the report that found this), while leaving the applied ones to be sent again.
      [...result.applied, ...result.rejected].forEach(action => {
        const i = findPendingActionIndex(pending, action)
        if (i < 0) { return }
        pending.splice(i, 1)
      });

      // Add all the rejected actions to the errored list (they are also still pending)
      errored.push(...result.error)

      // Errored actions stay pending and are retried forever. That's what we want
      // for a client that's offline or racing another write, but not for a record
      // the server has definitively refused — that one can never succeed, so it
      // wedges the queue and every later sync throws on it. Count the rejections,
      // and once an action has burned through its attempts drop it and remember
      // that we did, so sync-down doesn't just queue it up again.
      // The discard is reported to Sentry by the SyncActor.
      result.error.forEach((errorAction) => {
        const queued = findPendingAction(pending, errorAction)
        if (!queued) { return }
        if (!isServerRejection(errorAction.error)) { return }

        if (isUnrecoverable(queued, errorAction.error)) {
          pending.splice(pending.indexOf(queued), 1)

          // `errored` accumulates one entry per failed attempt, so it's a poor record
          // of what went wrong — replace this action's pile of duplicates with the
          // single unsyncable entry below, which is what the UI will read.
          for (let i = errored.length - 1; i >= 0; i--) {
            if (errored[i].table === errorAction.table &&
                errored[i].type === errorAction.type &&
                errored[i].record.id === errorAction.record.id) {
              errored.splice(i, 1)
            }
          }

          const unsyncable = (state.unsyncable ||= {})
          const forTable = (unsyncable[errorAction.table] ||= [])
          const previous = forTable.findIndex((r) => r.id === errorAction.record.id)
          if (previous >= 0) { forTable.splice(previous, 1) }
          forTable.push({
            id: errorAction.record.id,
            updated_at: errorAction.record.updated_at,
            type: errorAction.type,
            code: (errorAction.error as PostgrestError)?.code,
            message: errorAction.error?.message,
            discarded_at: new Date().toISOString(),
          })
        } else {
          queued.attempts = (queued.attempts ?? 0) + 1
        }
      })

      state.lastSync = result
      state.lastSyncedAt = Date.now()
    },

    /**
     * Store the watermark the server just issued. Verbatim — see the field's note on why
     * this must never be derived from our own records.
     */
    setSyncCursor: (state, action: PayloadAction<string>) => {
      state.cursor = action.payload
    },

    /**
     * Drop the watermark so the next sync-down does a full reconcile.
     *
     * Two callers: switching membership, and any sync failure. The second is the important
     * one — the fast path only asks for what changed after a point, so if a pass is lost
     * the rows it would have carried are simply never requested again. Falling back to the
     * full diff is how the client recovers rather than sitting on a quiet gap.
     */
    resetSyncCursor: (state) => {
      state.cursor = null
    },
  },
  extraReducers: (builder) => {
    builder = SyncActions.reduce((b, [type, table, action]) =>
      b.addCase(action, (state, {payload}) => {
        if (type != 'delete') {
          // Omit postgres generated columns
          if (isTableWithGeneratedColumns(table)) {
            payload = omit(payload, ...GeneratedColumns[table])
          }
        }
        
        if (type == 'update') {
          // If the previous pending action is an update for the same record, we can merge them
          const previous = state.pending[state.pending.length - 1]
          if (previous?.type === 'update' &&
              previous.table === table &&
              previous.record.id === payload.id &&
              previous.record.updated_at <= payload.updated_at
            ) {
              payload = merge(previous.record, payload)
              state.pending.pop() // remove the previous action b/c we've merged it with this one
          }
        }
        
        // We could have multiple actions for the same record that need to be applied in order,
        // ex: addExpense, updateExpense, deleteExpense
        // If typescript complains about this, it means one of the above actions has an incorrect payload.
        // The payload must be a Row of a SyncableTable
        state.pending.push({
          type,
          table,
          record: payload,
          // What the member did, not just what changed. The backend needs this to decide
          // whether a todo that has disappeared should be kept as history or dropped —
          // see `completed_with_actions` and ChangeTypes on the server.
          action_type: action.type
        } as SyncAction)

        // The record changed since we gave up on it, so it's worth trying again —
        // whatever the server objected to may well be gone now.
        clearUnsyncable(state, table, payload)

        if (isDeleted(payload)) {
          state.deletedLocally ||= {}
          const deletedLocally = state.deletedLocally[table] ||= []
          deletedLocally.push({
            id: payload.id,
            updated_at: payload.updated_at,
            deleted_at: payload.deleted_at
          })
        }
      }),
      builder)

    builder = builder.addCase(onSyncDownComplete, (state, action: PayloadAction<OnSyncDownCompletePayload>) => {
      const {pending, errored} = state;

      // When we load new data from the server, we want to remove any pending actions
      // that are no longer valid
      const payload = action.payload
      for (const table of SyncableTables) {
        const records = payload[table]
        if (!records) { continue }

        const {updated, deleted} = records
        updated?.forEach((record) => {
          removeIfObsolete(pending, record)
          removeIfObsolete(errored, record)
          // The server has the record after all, so there's nothing left to refuse.
          clearUnsyncable(state, table, record)
        })
        deleted?.forEach((record) => {
          removeIfObsolete(pending, record)
          removeIfObsolete(errored, record)
          clearUnsyncable(state, table, record)

          const deletedLocally = state.deletedLocally?.[table]
          if (deletedLocally) {
            const i = deletedLocally?.findIndex((r) => r.id === record.id)
            if (i !== undefined && i >= 0) {
              deletedLocally.splice(i, 1)
            }
          }
        })
      }

      if (payload.syncActions) {
        state.pending.push(...payload.syncActions)
      }

    })
    
    // The following special cases can be dispatched from a TODO action,
    // so the payload cannot include the timestamps because it gets serialized to JSON.
    // Therefore we have to duplicate the logic here — including the clearUnsyncable
    // call that the generated cases above get for free. Without it a record we'd
    // given up on would stay listed as failed-to-upload while it sits in the queue.
    builder = builder.addCase(markSubmissionAsRepaid, (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload
      
      const now = new Date().toISOString()
      
      const record = {
        id,
        updated_at: now,
        repaid_at: now
      }
      state.pending.push({
        type: 'update',
        table: 'submissions',
        record,
        action_type: markSubmissionAsRepaid.type
      })
      clearUnsyncable(state, 'submissions', record)
    })
    
    builder = builder.addCase(markHasCalledCHMForPregnancy, (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload

      const now = new Date().toISOString()

      const record = {
        id,
        updated_at: now,
        maternity_chm_call_completed_at: now
      }
      state.pending.push({
        type: 'update',
        table: 'incidents',
        record,
        // Named directly by IncidentTodoGenerator's completed_with_actions, so losing it
        // here would turn a completed maternity-call todo into a deleted one.
        action_type: markHasCalledCHMForPregnancy.type
      })
      clearUnsyncable(state, 'incidents', record)
    })

    builder = builder.addCase(completeExpensePayment, (state, action: PayloadAction<CompleteExpensePaymentPayload>) => {
      const { expenseId, paymentAmount, paymentDate } = action.payload
      
      const now = new Date().toISOString()

      const newPaymentHistory = cloneDeep(action.payload.currentPaymentHistory)
      newPaymentHistory.payments.push({
        amount: paymentAmount,
        date: paymentDate
      })
      
      const record = {
        id: expenseId,
        updated_at: now,
        is_fully_paid: true,
        payment_history: newPaymentHistory
      }
      state.pending.push({
        type: 'update',
        table: 'expenses',
        record,
        action_type: completeExpensePayment.type
      })
      clearUnsyncable(state, 'expenses', record)
    })
    

    builder = builder.addCase(PURGE, (state) => {
      return initialState
    })

    return builder
  }
})

export type SyncSliceAction = ReturnType<typeof syncSlice.actions[keyof typeof syncSlice.actions]> |
  ReturnType<typeof onSyncDownComplete>

export function isSyncSliceAction(action: any): action is SyncSliceAction {
  return action.type?.startsWith(syncSlice.name)
}

export const { onSyncUpComplete, setSyncCursor, resetSyncCursor } = syncSlice.actions

export default syncSlice.reducer

/**
 * Forget that we gave up on a record, so it gets a fresh set of attempts. Called
 * whenever a newer version of it shows up — edited locally, or arriving from the
 * server (in which case there's nothing left to sync up anyway).
 */
function clearUnsyncable(
  state: SyncSliceState,
  table: SyncableTable,
  record: { id: string, updated_at: string }
): void {
  const forTable = state.unsyncable?.[table]
  if (!forTable) { return }

  const i = forTable.findIndex((r) => r.id === record.id && r.updated_at <= record.updated_at)
  if (i >= 0) { forTable.splice(i, 1) }
}

/**
 * Everything this client failed to upload, newest first, flattened across tables.
 *
 * An entry means the row on screen is not on the server and no longer being retried,
 * so this is what a "didn't save" indicator should be driven from. Entries clear
 * themselves once the record changes again (see clearUnsyncable), so a row that's
 * back in the queue stops reporting as failed without the UI having to track it.
 */
export function selectUnsyncableRecords(
  state: Pick<SyncSliceState, 'unsyncable'>
): Array<UnsyncableRecord & { table: SyncableTable }> {
  return SyncableTables
    .flatMap((table) =>
      (state.unsyncable?.[table] || []).map((record) => ({ ...record, table })))
    .sort((a, b) => b.discarded_at.localeCompare(a.discarded_at))
}

/**
 * Whether sync-down should leave this record alone because we already gave up on
 * syncing it. A newer local version clears the entry (see clearUnsyncable), so this
 * only suppresses the exact version the server refused.
 */
export function isUnsyncable(
  state: Pick<SyncSliceState, 'unsyncable'>,
  table: SyncableTable,
  record: { id: string, updated_at: string }
): boolean {
  return !!state.unsyncable?.[table]?.some((r) =>
    r.id === record.id && r.updated_at >= record.updated_at)
}

/**
 * Remove a pending or errored action if it is made obsolete by a newer version of the record
 */
function removeIfObsolete<T extends SyncableTable>(pending: SyncAction<T>[], updatedRecord: SyncAction<T>['record']): void {
  const toRemove = pending.filter((t) => t.record.id === updatedRecord.id && t.record.updated_at <= updatedRecord.updated_at)
  toRemove.forEach((t) => {
    const i = pending.indexOf(t)
    // Remove the pending action
    pending.splice(i, 1)
  })
}

export function redactSyncSlice(state: SyncSliceState): DeepNullable<SyncSliceState> {
  return {
    // unsyncable holds only ids, timestamps and error codes — no PHI, and it's the
    // first thing you want to see when a client reports missing data. `cursor` is a bare
    // timestamp and is the next thing you want: a stuck one explains missing rows.
    ...pick(state, 'lastSyncedAt', 'error', 'unsyncable', 'cursor'),
    pending: state.pending.map(redactSyncAction),
    errored: state.errored.map(redactSyncAction),
    lastSync: state.lastSync ? {
      ...pick(state.lastSync, 'updated_at'),
      applied: state.lastSync.applied.map(redactSyncAction),
      rejected: state.lastSync.rejected.map(redactSyncAction),
      error: state.lastSync.error.map(redactSyncAction),
    } : null
  } as DeepNullable<SyncSliceState>
}

export function redactSyncSliceAction(action: SyncSliceAction) {

  switch(action.type) {
    case onSyncDownComplete.type:
      return {
        type: action.type,
        payload: {
          expenses: action.payload.expenses && {
            updated: action.payload.expenses.updated?.map(redactExpense),
            deleted: action.payload.expenses.deleted?.map(redactExpense)
          },
          incidents: action.payload.incidents && {
            updated: action.payload.incidents.updated?.map(redactIncident),
            deleted: action.payload.incidents.deleted?.map(redactIncident)
          },
          submissions: action.payload.submissions && {
            updated: action.payload.submissions.updated?.map(redactSubmission),
            deleted: action.payload.submissions.deleted?.map(redactSubmission)
          },
          attachments: action.payload.attachments && {
            updated: action.payload.attachments.updated?.map(redactAttachment),
            deleted: action.payload.attachments.deleted?.map(redactAttachment)
          },
          providers: action.payload.providers && {
            updated: action.payload.providers.updated?.map(redactProvider),
            deleted: action.payload.providers.deleted?.map(redactProvider)
          },
          advances: action.payload.advances && {
            updated: action.payload.advances.updated?.map(redactAdvance),
            deleted: action.payload.advances.deleted?.map(redactAdvance)
          },
          advance_requests: action.payload.advance_requests && {
            updated: action.payload.advance_requests.updated?.map(redactAdvanceRequest),
            deleted: action.payload.advance_requests.deleted?.map(redactAdvanceRequest)
          },
          notifications: action.payload.notifications && {
            updated: action.payload.notifications.updated?.map(redactNotification),
            deleted: action.payload.notifications.deleted?.map(redactNotification)
          },
          todos: action.payload.todos && {
            updated: action.payload.todos.updated?.map(redactTodo),
            deleted: action.payload.todos.deleted?.map(redactTodo)
          },
          blobs: action.payload.blobs && {
            updated: action.payload.blobs.updated?.map(redactBlob),
            deleted: action.payload.blobs.deleted?.map(redactBlob)
          },
        }
      }
    case onSyncUpComplete.type:
      return {
        type: action.type,
        payload: {
          ...pick(action.payload, 'updated_at'),
          applied: action.payload.applied.map(redactSyncAction),
          rejected: action.payload.rejected.map(redactSyncAction),
          error: action.payload.error.map(redactSyncAction),
        }
      }

    default:
      return {
        type: (action as any).type,
        payload: 'REDACTED'
      }
  }
}

function redactSyncAction<T extends SyncAction>(action: T): T {
  return {
    ...pick(action, 'type', 'table', 'attempts'),
    record: pick(action.record, 'id', 'updated_at')
  } as T
}
